import {
  Claim,
  ClaimFileDownloadLink,
  ClaimFilesPageSize,
  ClaimFilesResponse,
  ClaimMessage,
  getClaimById,
  getClaimFileDownloadLink,
  getClaimFiles,
  getClaimMessageAttachmentDownloadLink,
  getClaimMessages,
  getUnreadCount,
  markClaimMessageRead,
  sendClaimMessage,
  UnreadCount,
  uploadClaimFiles,
  UploadClaimFilesResponse,
} from '@/api/claims'
import { useSnackbar } from '@/providers'
import * as Sentry from '@sentry/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query'

export const useClaim = (
  companyId: string | undefined,
  claimId: string | undefined,
) =>
  useQuery(
    ['claim', companyId, claimId],
    () =>
      getClaimById(companyId as string, claimId as string).then(
        (response) => response.data as Claim,
      ),
    { enabled: !!companyId && !!claimId },
  )

const unreadCountQuery = (companyId: string) => ({
  queryKey: ['unread-count', companyId],
  queryFn: () =>
    getUnreadCount(companyId).then((response) => response.data as UnreadCount),
  enabled: !!companyId,
})

export const useUnreadCount = (companyId: string) =>
  useQuery(unreadCountQuery(companyId))

export const useUnreadCounts = (companyIds: string[]) => {
  const results = useQueries(companyIds.map(unreadCountQuery))

  return {
    total: results.reduce((sum, result) => sum + (result.data?.total ?? 0), 0),
    isLoading: results.some((result) => result.isLoading),
  }
}

const MESSAGES_PAGE_SIZE = 50

export const useClaimMessages = (
  companyId: string | undefined,
  claimId: string | undefined,
  search: string,
) => {
  const { t } = useTranslation()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()
  const [messages, setMessages] = useState<ClaimMessage[]>([])
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)

  const { isLoading, isError, data } = useQuery(
    ['claim-messages', companyId, claimId, search],
    () =>
      getClaimMessages(companyId as string, claimId as string, {
        search,
      }).then((response) => response.data as ClaimMessage[]),
    { enabled: !!companyId && !!claimId },
  )

  useEffect(() => {
    if (isLoading) {
      setMessages([])
      setHasMore(false)
      return
    }

    if (!data) return
    setMessages(data)
    setHasMore(data.length >= MESSAGES_PAGE_SIZE)
  }, [data, isLoading])

  const currentQueryRef = useRef({ companyId, claimId, search })
  currentQueryRef.current = { companyId, claimId, search }

  const loadMore = async () => {
    const lastMessage = messages[messages.length - 1]
    if (!companyId || !claimId || !lastMessage) return

    const requestQuery = { companyId, claimId, search }
    setIsLoadingMore(true)
    try {
      const nextPage = await getClaimMessages(companyId, claimId, {
        search,
        before: lastMessage.sent_at,
      }).then((response) => response.data as ClaimMessage[])

      const isStale =
        currentQueryRef.current.companyId !== requestQuery.companyId ||
        currentQueryRef.current.claimId !== requestQuery.claimId ||
        currentQueryRef.current.search !== requestQuery.search

      if (isStale) return

      setMessages((prev) => [...prev, ...nextPage])
      setHasMore(nextPage.length >= MESSAGES_PAGE_SIZE)
    } finally {
      setIsLoadingMore(false)
    }
  }

  const sendMutation = useMutation(
    (message: {
      subject?: string
      body: string
      file_ids?: string[]
      reply_to_item_id?: string
    }) =>
      sendClaimMessage(companyId as string, claimId as string, message).then(
        (response) => {
          const sent = response.data as ClaimMessage
          return { ...sent, attachments: sent.attachments ?? [] }
        },
      ),
    {
      onSuccess: (newMessage) => {
        setMessages((prev) => [newMessage, ...prev])
        // The optimistic echo carries no attachments (provider ids aren't known
        // yet); refetch so a sent message with attachments reconciles.
        queryClient.invalidateQueries(['claim-messages', companyId, claimId])
      },
      onError: (error) => {
        Sentry.captureException(error)
        snackbar.error(t('snackbars.error.generalMsg'))
      },
    },
  )

  const downloadAttachmentMutation = useMutation(
    ({ itemId, attachmentId }: { itemId: string; attachmentId: string }) =>
      getClaimMessageAttachmentDownloadLink(
        companyId as string,
        claimId as string,
        itemId,
        attachmentId,
      ).then((response) => response.data as ClaimFileDownloadLink),
    {
      onSuccess: ({ url }) => {
        window.location.href = url
      },
      onError: (error) => {
        Sentry.captureException(error)
        snackbar.error(t('snackbars.error.generalMsg'))
      },
    },
  )

  const markReadMutation = useMutation(
    (itemId: string) =>
      markClaimMessageRead(companyId as string, claimId as string, itemId),
    {
      onSuccess: (_, itemId) => {
        setMessages((prev) =>
          prev.map((message) =>
            message.item_id === itemId
              ? { ...message, is_unread: false }
              : message,
          ),
        )
        queryClient.invalidateQueries(['unread-count', companyId])
      },
      onError: (error) => {
        Sentry.captureException(error)
        snackbar.error(t('snackbars.error.generalMsg'))
      },
    },
  )

  return {
    messages,
    isLoading,
    isError,
    hasMore,
    isLoadingMore,
    loadMore,
    sendMessage: sendMutation.mutate,
    isSending: sendMutation.isLoading,
    markRead: markReadMutation.mutate,
    downloadAttachment: downloadAttachmentMutation.mutate,
  }
}

export const useUploadClaimFiles = (
  companyId: string | undefined,
  claimId: string | undefined,
) => {
  const { t } = useTranslation()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()
  const listQueryKey = ['claim-files', companyId, claimId]

  const uploadMutation = useMutation(
    (files: File[]) =>
      uploadClaimFiles(companyId as string, claimId as string, files).then(
        (response) => response.data as UploadClaimFilesResponse,
      ),
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries(listQueryKey)
        if (result.failed.length > 0) {
          snackbar.error(
            t('claimsPage.detail.files.uploadPartialError', {
              files: result.failed.join(', '),
            }),
          )
        }
      },
      onError: (error) => {
        Sentry.captureException(error)
        snackbar.error(t('snackbars.error.generalMsg'))
      },
    },
  )

  return {
    uploadFiles: uploadMutation.mutate,
    isUploading: uploadMutation.isLoading,
  }
}

export const useClaimFiles = (
  companyId: string | undefined,
  claimId: string | undefined,
  search: string,
  page: number,
  pageSize: ClaimFilesPageSize,
) => {
  const { t } = useTranslation()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()
  const listQueryKey = ['claim-files', companyId, claimId]
  const queryKey = [...listQueryKey, search, page, pageSize]

  const { isLoading, isFetching, isError, data } = useQuery(
    queryKey,
    () =>
      getClaimFiles(companyId as string, claimId as string, {
        search,
        page,
        pageSize,
      }).then((response) => response.data as ClaimFilesResponse),
    {
      enabled: !!companyId && !!claimId,
      keepPreviousData: true,
      onError: (error) => {
        Sentry.captureException(error)
      },
    },
  )

  const { uploadFiles, isUploading } = useUploadClaimFiles(companyId, claimId)

  const downloadMutation = useMutation(
    (fileId: string) =>
      getClaimFileDownloadLink(
        companyId as string,
        claimId as string,
        fileId,
      ).then((response) => response.data as ClaimFileDownloadLink),
    {
      onSuccess: ({ url }) => {
        window.location.href = url
        queryClient.invalidateQueries(listQueryKey)
        queryClient.invalidateQueries(['unread-count', companyId])
      },
      onError: (error) => {
        Sentry.captureException(error)
        snackbar.error(t('snackbars.error.generalMsg'))
      },
    },
  )

  return {
    files: data?.data ?? [],
    totalItems: data?.meta.totalItems ?? 0,
    isLoading,
    isFetching,
    isError,
    uploadFiles,
    isUploading,
    downloadFile: downloadMutation.mutate,
  }
}
